/**
 * THE DOCUMENTATION BUNDLE AND MANIFEST — plan-019 DOM (ED-14, ED-15; design §31.11.1, §31.6.4; ADR 0065, proposed;
 * glossary §6.33 "Help source", "Documentation bundle", "Help documentation build"). Contract IC-H3 (the type), read
 * by DOCS (writes it), WRK-DOCS (stores it in `help_documentation_build.manifest`), HOST-B (links) and EVAL-H.
 *
 * The bundle is eight Markdown documents, `help-<kind>.<lang>.md`, with file names constant across builds; its SHA-256
 * over the eight files in name order is the documentation build id. The manifest lists every page: a stable key across
 * languages and builds, the kind, the language, the file name, the page's `# ` heading exactly, and the link.
 */
import { HELP_LANGUAGE, isHelpLanguage } from "./surface.js";
/** `help_source`: the four kinds of wetalk's own documentation the bundle holds (legal pages are never indexed, OD-22). */
export const HELP_SOURCE = ["public_docs", "public_page", "portal_docs", "api_reference"];
/** The file-name segment of each source. */
export const HELP_SOURCE_FILE_SEGMENT = Object.freeze({
    public_docs: "public-docs",
    public_page: "public-pages",
    portal_docs: "portal-docs",
    api_reference: "api-reference",
});
/** `help-<kind>.<lang>.md`. */
export function helpDocumentFileNameOf(source, language) {
    return `help-${HELP_SOURCE_FILE_SEGMENT[source]}.${language}.md`;
}
/** The eight documents of a bundle, in name order (the order the build id is computed in). */
export const HELP_DOCUMENT_FILE_NAME = Object.freeze(HELP_SOURCE.flatMap((source) => HELP_LANGUAGE.map((language) => helpDocumentFileNameOf(source, language))).sort());
/** A documentation build id: the bundle's SHA-256, lowercase hex. */
export const DOCUMENTATION_BUILD_ID_PATTERN = /^[0-9a-f]{64}$/;
/**
 * The documentation build id: SHA-256 (lowercase hex) over the eight documents' bytes concatenated in file-name order
 * ({@link HELP_DOCUMENT_FILE_NAME}; design §31.11.1). ONE function for the two places that compute it — `tools/help-docs`,
 * which writes `digest.txt`, and the worker that materialises the baked bundle and refuses one whose bytes no longer
 * hash to it (plan-019 WRK-DOCS, moved here from `tools/help-docs/src/bundle.ts` so the worker never imports `tools/`).
 * Every other entry of `document` (the manifest, the digest file) is ignored. A missing document raises.
 */
export function documentationBuildIdOf(document, hash) {
    for (const name of HELP_DOCUMENT_FILE_NAME) {
        const bytes = document.get(name);
        if (bytes === undefined)
            throw new DocumentationManifestError(`the bundle has no ${name}`);
        hash.update(bytes);
    }
    return hash.digest("hex");
}
/** A passage's first line splits on this into `[file name, page title, section …]` (Plan A, design §31.6.4). */
export const HELP_PASSAGE_HEAD_SEPARATOR = " › ";
/** Thrown when a stored or generated manifest is not exactly one; the message names the first fault. */
export class DocumentationManifestError extends Error {
    name = "DocumentationManifestError";
}
const KEY = /^(public_docs|public_page|portal_docs|api_reference):[A-Za-z0-9][A-Za-z0-9._/#:-]{0,199}$/;
function pageOf(value, index) {
    const fault = (what) => {
        throw new DocumentationManifestError(`manifest page ${String(index)}: ${what}`);
    };
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return fault("not an object");
    const f = value;
    const own = Object.keys(f).sort().join(",");
    if (own !== "fileName,href,key,kind,language,title")
        return fault(`fields ${own}`);
    const { key, kind, language, fileName, title, href } = f;
    if (typeof kind !== "string" || !HELP_SOURCE.includes(kind))
        return fault("kind");
    if (typeof key !== "string" || !KEY.test(key) || !key.startsWith(`${kind}:`))
        return fault("key");
    if (!isHelpLanguage(language))
        return fault("language");
    if (fileName !== helpDocumentFileNameOf(kind, language))
        return fault("fileName");
    if (typeof title !== "string" || title.trim() !== title || title.length === 0 || title.length > 200 || /[\r\n]/.test(title)) {
        return fault("title");
    }
    if (typeof href !== "string" || !href.startsWith("/") || href.startsWith("//") || /\s/.test(href))
        return fault("href");
    return { key, kind: kind, language, fileName, title, href };
}
/**
 * The manifest, checked: a build id, and pages each exactly shaped, whose file name matches its kind and language, with
 * no two pages sharing a key and a language and no two sharing a file name and a title (Plan A's look-up key).
 */
export function documentationManifestOf(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new DocumentationManifestError("manifest: not an object");
    }
    const f = value;
    if (Object.keys(f).sort().join(",") !== "documentationBuildId,page")
        throw new DocumentationManifestError("manifest: fields");
    const id = f["documentationBuildId"];
    if (typeof id !== "string" || !DOCUMENTATION_BUILD_ID_PATTERN.test(id)) {
        throw new DocumentationManifestError("manifest: documentationBuildId");
    }
    const raw = f["page"];
    if (!Array.isArray(raw))
        throw new DocumentationManifestError("manifest: page");
    const page = raw.map(pageOf);
    const byKey = new Set();
    const byHead = new Set();
    for (const entry of page) {
        const keyed = `${entry.key}\u0000${entry.language}`;
        const head = `${entry.fileName}\u0000${entry.title}`;
        if (byKey.has(keyed))
            throw new DocumentationManifestError(`manifest: key ${entry.key} twice in ${entry.language}`);
        if (byHead.has(head))
            throw new DocumentationManifestError(`manifest: title "${entry.title}" twice in ${entry.fileName}`);
        byKey.add(keyed);
        byHead.add(head);
    }
    return { documentationBuildId: id, page };
}
//# sourceMappingURL=manifest.js.map